import {EyeIcon} from "@/components/shared";
import {CircularProgress} from "@nextui-org/react";
import React from "react";
import Loader from "../Loader";

interface DriverComplianceProps {
  isLoading?: boolean;
  instituteuserids?: any;
  driverComplianceSummary: DriverComplianceSummary;
  openPopup?: (title: popupParams) => void;
  usersetting?: () => void;
}

interface DriverComplianceSummary {
  total: number;
  expiringSoon: {
    days: number;
    count: number;
  };
  categories: {
    typeId: number;
    typeName: string;
    total: number;
    active: number;
    expired: number;
    noData: number;
  }[];
}

const defaultDriverComplianceSummary: DriverComplianceSummary = {
  total: 0,
  expiringSoon: {
    days: 0,
    count: 0
  },
  categories: []
};

const placeholderCategories: DriverComplianceSummary["categories"] = [
  {typeId: 1, typeName: "Licence", total: 0, active: 0, expired: 0, noData: 0},
  {typeId: 2, typeName: "Badge", total: 0, active: 0, expired: 0, noData: 0},
  {
    typeId: 3,
    typeName: "Medical Test",
    total: 0,
    active: 0,
    expired: 0,
    noData: 0
  },
  {typeId: 4, typeName: "PVC", total: 0, active: 0, expired: 0, noData: 0},
  {typeId: 5, typeName: "DDT", total: 0, active: 0, expired: 0, noData: 0},
  {typeId: 6, typeName: "BGV", total: 0, active: 0, expired: 0, noData: 0}
];

const ringIndicatorClass = {
  success: "stroke-[#009759]",
  danger: "stroke-[#EA580C]"
} as const;

const getRingDisplay = (metric: {
  active: number;
  expired: number;
  noData: number;
}) => {
  const active = metric.active ?? 0;
  const expired = metric.expired ?? 0;
  const noData = metric.noData ?? 0;
  const total = active + expired + noData;
  const status: keyof typeof ringIndicatorClass =
    active >= expired ? "success" : "danger";
  const leadingCount = status === "success" ? active : expired;
  const percentage = total ? Math.round((leadingCount / total) * 100) : 0;
  return {percentage, status};
};

const ComplianceRing: React.FC<{
  percentage: number;
  status: keyof typeof ringIndicatorClass;
}> = ({percentage, status}) => (
  <CircularProgress
    aria-label={`${percentage}% compliant`}
    size="lg"
    value={percentage}
    valueLabel={`${percentage}%`}
    showValueLabel
    strokeWidth={4}
    classNames={{
      svg: "h-16 w-16 drop-shadow-sm",
      indicator: ringIndicatorClass[status],
      track: "stroke-gray-200",
      value: "text-sm font-bold text-gray-800"
    }}
  />
);

const DriverComplianceWidget: React.FC<DriverComplianceProps> = ({
  isLoading = false,
  driverComplianceSummary,
  openPopup
}) => {
  const summary = driverComplianceSummary ?? defaultDriverComplianceSummary;
  const metrics =
    summary.categories && summary.categories.length > 0
      ? summary.categories
      : placeholderCategories;

  return (
    <div className="rounded-xl border bg-neo-white p-2">
      {/* Header */}
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="text-lg font-semibold">Driver Compliance Summary</div>
        </div>
        <h5
          className="cursor-pointer pr-1"
          title="View"
          onClick={() => openPopup?.({title: "Driver Compliance Summary"})}
        >
          <EyeIcon size={18} />
        </h5>
      </div>

      {isLoading ? (
        <Loader />
      ) : (
        <div className="flex flex-col gap-1">
          {/* Summary banner */}
          <div className="mb-1 flex items-center justify-between rounded-xl bg-[#FEF3E2] px-3 py-2">
            <div className="flex items-center gap-6">
              <div className="flex flex-row items-center gap-2">
                <div className="text-sm text-gray-500">Total</div>
                <div className="text-xl font-bold text-gray-900">
                  {summary.total ?? 0}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div>
                <div className="text-xs font-semibold text-[#EA580C]">
                  Expiring
                </div>
                <div className="text-xs text-gray-500">Soon</div>
              </div>

              <div className="flex items-center gap-1 rounded-full bg-white px-3 py-1.5">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M11 0L2 11H9L8 20L18 8H10L11 0Z" fill="#F28A03" />
                </svg>
                <span className="text-xs font-medium text-gray-700">
                  {summary.expiringSoon?.days ?? 0} days
                </span>
                <span className="text-xs font-bold text-[#2563EB]">
                  {summary.expiringSoon?.count ?? 0} Drivers
                </span>
              </div>
            </div>
          </div>

          {/* Rings grid */}
          <div className="grid grid-cols-3 gap-2">
            {metrics.map(metric => {
              return (
                <div
                  key={metric.typeId}
                  className="flex flex-col items-center gap-1"
                >
                  <ComplianceRing {...getRingDisplay(metric)} />
                  <span className="text-center text-xs font-bold text-gray-800">
                    {metric.typeName}
                  </span>
                  <div className="flex items-center justify-center gap-2 text-[11px]">
                    <span className="flex items-center gap-1">
                      <span className="h-1.5 w-1.5 rounded-full bg-[#009759]" />
                      {metric.active ?? 0}
                    </span>
                    <span className="flex items-center gap-1 ">
                      <span className="h-1.5 w-1.5 rounded-full bg-[#EA580C]" />
                      {metric.expired ?? 0}
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="h-1.5 w-1.5 rounded-full bg-gray-300" />
                      {metric.noData ?? 0}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
          {/* Legend */}
          <div className="mt-1 flex items-center justify-center gap-4 text-[11px] ">
            <span className="flex items-center gap-1">
              <span className="h-1.5 w-1.5 rounded-full bg-[#009759]" />
              Active
            </span>
            <span className="flex items-center gap-1">
              <span className="h-1.5 w-1.5 rounded-full bg-[#EA580C]" />
              Expired
            </span>
            <span className="flex items-center gap-1">
              <span className="h-1.5 w-1.5 rounded-full bg-gray-300" />
              No Data
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default DriverComplianceWidget;
